import api from "../../services/api";
import { Eventbus } from "../../utils";
import { events } from "../../utils/constants";
import { debounce } from "../../utils/helpers";
import logger from "../../utils/logger";
const landingService = (() => {
    let data = {};
    let currentIsAdmin = false;

    function sortSummaryByType(summaryByType) {
        if (!Array.isArray(summaryByType)) {
            return [];
        }

        return [...summaryByType].sort((a, b) => a.typeName?.localeCompare(b.typeName));
    }

    function mergeCentreBankingStats(existingStats = {}, centreBankingStats = {}) {
        const {
            summaryByType: _summaryByType,
            totalTransactions: _totalTransactions,
            ...safeCentreBankingStats
        } = centreBankingStats || {};

        const mergedStats = {
            ...existingStats,
            ...safeCentreBankingStats
        };

        if (Array.isArray(existingStats.summaryByType)) {
            mergedStats.summaryByType = sortSummaryByType(existingStats.summaryByType);
        }

        if (existingStats.totalTransactions !== undefined) {
            mergedStats.totalTransactions = existingStats.totalTransactions;
        }

        return mergedStats;
    }

    // Private function to reload stats and emit specified event
    async function getStats(eventType, isAdmin = false) {
        try {
            // Fetch stats concurrently - only fetch transaction stats if admin
            const promises = isAdmin 
                ? [api.getStatus(), api.getCentreBankingStats()]
                : [api.getCentreBankingStats()];
            
            const results = await Promise.all(promises);
            const transactionStats = isAdmin ? results[0] : null;
            const centreBankingStats = isAdmin ? results[1] : results[0];

            // Handle cancellation (only relevant for admin)
            if (isAdmin && transactionStats && transactionStats.code === 'ERR_CANCELED') {
                if (eventType === events.STATS.STATUS_LOADED) {
                    Eventbus.emit(eventType, transactionStats);
                }
                return transactionStats;
            }

            // Merge stats - only include transaction stats if admin
            const mergedStats = isAdmin 
                ? {
                    ...transactionStats,
                    ...centreBankingStats
                }
                : {
                    ...centreBankingStats
                };

            // Sort transaction types (only if admin and data exists)
            if (isAdmin) {
                mergedStats.summaryByType = sortSummaryByType(mergedStats.summaryByType);
            }
            data.stats = mergedStats;
            
            // Emit the specified event
            Eventbus.emit(eventType, mergedStats);
            return mergedStats;
        } catch (error) {
            logger.error('Error reloading stats:', error);
            throw error;
        }
    }

    // Debounced function to reload stats data for live updates
    const debouncedReload = debounce(() => {
        getStats(events.STATS.STATUS_UPDATED, currentIsAdmin);
    }, 500);

    // Debounced function to reload centre banking stats with 20-second debounce
    const debouncedCentreBankingReload = debounce(() => {
        api.getCentreBankingStats().then(centreBankingStats => {
            if (data.stats) {
                data.stats = currentIsAdmin
                    ? mergeCentreBankingStats(data.stats, centreBankingStats)
                    : {
                        ...data.stats,
                        ...centreBankingStats
                    };
                Eventbus.emit(events.STATS.STATUS_UPDATED, data.stats);
            }
        }).catch(error => {
            logger.error('Error reloading centre banking stats:', error);
        });
    }, 20000);

    function reloadAfterCentreBankingChange() {
        if (currentIsAdmin) {
            debouncedReload();
            return;
        }

        debouncedCentreBankingReload();
    }

    // Set up event listeners for live updates
    Eventbus.on(events.SIGNALR.BANKFILE_DATA_CHANGES, () => {
        debouncedReload();
    });
    Eventbus.on(events.SIGNALR.EXPORT_DATA_CHANGES, () => {
        debouncedReload();
    });
    Eventbus.on(events.SIGNALR.EXPORT_DELETED, () => {
        debouncedReload();
    });
    Eventbus.on(events.SIGNALR.CENTRE_BANKING_MERCHANT_FILE_UPLOADED, () => {
        debouncedReload();
        debouncedCentreBankingReload();
    });
    Eventbus.on(events.SIGNALR.TRANSACTION_SPLIT_DATA_CHANGES, () => {
        reloadAfterCentreBankingChange();
    });
    Eventbus.on(events.SIGNALR.TRANSACTION_SPLIT_DATA_CHANGES_DELETE, () => {
        reloadAfterCentreBankingChange();
    });

    function getData() {
        return data;
    }

    function loadData(isAdmin = false) {
        currentIsAdmin = isAdmin;
        return getStats(events.STATS.STATUS_LOADED, isAdmin);
    }

    return {
        getData,
        loadData,
    };
})();

export default landingService;